import Text from 'common/ui/Text';
import { css } from 'styled-components';
import TextWithTooltip from 'common/TextWithTooltip';
import { Stack } from '../ui/Layout';
import { Colors } from 'styles';
import { toLocaleTimeString, toLocaleString } from 'utils/date';
import { StudyLabelChip } from 'domains/PatientJacket/common/StudyLabelChip';
import Skeleton from '@mui/material/Skeleton';
import type { FlattenSimpleInterpolation } from 'styled-components';
import { getStringValueOrNA, isEmptyStringOrNull } from 'utils/string';
import { rootTheme } from 'theme/rootTheme';

export const formatStudyInformation = (
  studyDescription?: string | null,
  accessionNumber?: string | null,
  studyDate?: Date | null
): string => {
  const studyDescriptionText = isEmptyStringOrNull(studyDescription)
    ? 'Study Desc: N/A'
    : studyDescription;
  const accessionNumberText = getStringValueOrNA(accessionNumber);
  const formattedTime = toLocaleTimeString(studyDate, { hour: 'numeric' });
  const formattedDate = toLocaleString(studyDate, { year: '2-digit' });
  const studyDateText =
    studyDate != null && formattedTime != null && formattedDate != null
      ? `${formattedDate} ${formattedTime}`
      : 'Time: N/A';

  return `${studyDescriptionText} (ACC: ${accessionNumberText}), ${studyDateText}`;
};

export const formatStudyInformationWithoutDate = (
  studyDescription?: string | null,
  accessionNumber?: string | null
): string =>
  `${
    isEmptyStringOrNull(studyDescription) ? 'Study Desc: N/A' : studyDescription
  } (ACC: ${getStringValueOrNA(accessionNumber)})`;

export const VARIANT = Object.freeze({
  TOOLBAR: 'toolbar',
  PANEL: 'panel',
});
type VariantType = (typeof VARIANT)[keyof typeof VARIANT];

type StudyInformationCardProps = {
  studyColor: Nullable<string>;
  studyDescription: Nullable<string>;
  accessionNumber: Nullable<string>;
  studyDate: Nullable<Date>;
  variant?: VariantType;
  textCss?: FlattenSimpleInterpolation;
  studyLabel?: Nullable<string>;
  isLoading?: boolean;
};

export const StudyInformationCard = ({
  studyColor,
  studyDescription,
  accessionNumber,
  studyDate,
  variant = VARIANT.TOOLBAR,
  textCss,
  studyLabel,
  isLoading = false,
}: StudyInformationCardProps): React.ReactElement => {
  if (isLoading) {
    return <Skeleton height={32} width="100%" />;
  }

  return (
    <Stack
      alignX={variant === VARIANT.TOOLBAR ? 'center' : 'start'}
      alignY="center"
      css={css`
        user-select: none;
        flex: 1 1 auto;
        min-width: 0;
        padding-right: 24px;
      `}
      data-testid="study-information-card"
      space="xsmall"
    >
      {variant === VARIANT.TOOLBAR && (
        <div
          css={css`
            min-height: 12px;
            min-width: 12px;
            border-radius: ${rootTheme.radius.xxl}px;
            background-color: ${studyColor ?? Colors.gray5};
          `}
        />
      )}
      {variant === VARIANT.PANEL && studyLabel != null && (
        <StudyLabelChip studyColor={studyColor} studyLabel={studyLabel} />
      )}
      <Text
        variant={variant === VARIANT.PANEL ? 'button' : 'display1'}
        css={css`
          display: flex;
          width: 100%;
          ${textCss || ''}
        `}
      >
        <TextWithTooltip
          conditionalTooltip={true}
          name={formatStudyInformation(studyDescription, accessionNumber, studyDate)}
          copyableText={isEmptyStringOrNull(studyDescription) ? null : studyDescription}
          variant="noWrap"
          data-testid="study-information"
        />
      </Text>
    </Stack>
  );
};
